import React, { useEffect } from 'react'
import { EmtIcon, EmtModal, EmtTypography } from 'emt-components-v2'
import Box from '@mui/material/Box'
import { useTranslation } from 'next-i18next'
import NextImage from 'next/image'
import LinkButton from '@/components/EMT/LinkButton'
import { Routes } from '@/assets/configs'
import { useIsQueryOwner, useIsResponsive } from '@/hooks'
import { styled } from '@mui/material/styles'
import { gtmPageView } from '@/helpers/GTMHelper'
import { useRouter } from 'next/router'
import { appendParamsIfNeeded } from '@/utils'
import VariantImages from '@/assets/VariantImages'
import useCurrentUser from '@/hooks/useCurrentUser'

type Props = React.PropsWithChildren<{
  open: boolean
  onClose?: () => void
  isFixed?: boolean
  activeSecondaryContent?: boolean
  hideNavigation?: boolean
  activeGtmEvent?: boolean
  zIndex?: number
}>

const NoPremiumBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: '15px',
  textAlign: 'center',
  p: {
    fontSize: '1rem',
  },
  [theme.breakpoints.down('md')]: {
    textAlign: 'left',
    h4: {
      fontSize: '1.5rem',
    },
    h5: {
      fontSize: '1.15rem',
    },
  },
}))

const PremiumModal = ({
  open,
  onClose,
  isFixed,
  activeSecondaryContent,
  hideNavigation,
  activeGtmEvent = true,
  zIndex = 998,
}: Props) => {
  const { t } = useTranslation('message')
  const router = useRouter()

  const [isMobile] = useIsResponsive()
  const [isQueryOwner, queryType, appendQueryType] = useIsQueryOwner()
  const { currentUser } = useCurrentUser()

  // 📟Gtm event
  useEffect(() => {
    if (activeGtmEvent && open) {
      gtmPageView({
        pageUrl: appendParamsIfNeeded(appendQueryType(router.asPath), { poppremium: true }),
        pageTitle: document.querySelector('title')?.innerText as string,
        userId: currentUser?.id,
        userRole: isQueryOwner ? 'maitre' : 'emprunteur',
      })
    }
  }, [open])

  return (
    <EmtModal
      open={open}
      onClose={onClose}
      variant="large"
      disableScrollLock
      sx={{
        zIndex: zIndex,
        bottom: 0,
        '& .emt-x': {
          fontSize: '12px',
          color: (theme: any) => theme.palette.greyGradient['550'],
          zIndex: isMobile ? 9 : 'auto',
          marginTop: '0px!important',
        },
        ...(isMobile
          ? {
              ...(isFixed
                ? {
                    '& .inner-box': {
                      bottom: 0,
                      top: 'unset',
                      transform: 'translateX(-50%)',
                      width: '100%',
                      '& .children-box': {
                        bottom: hideNavigation ? '0' : '68px',
                        background: (theme: any) => theme.palette.common.white,
                        borderTopRightRadius: '24px',
                        borderTopLeftRadius: '24px',
                      },
                    },
                  }
                : {}),
            }
          : {
              '& .inner-box': {
                width: '605px',
                '& .children-box': {},
              },
            }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'start' : 'center',
          gap: '15px',
          paddingBottom: isMobile ? '50px' : '0',
        }}
      >
        <NextImage src={VariantImages.PremiumMessageImg} width={145} height={76} quality={100} alt="" />
        <NoPremiumBox>
          <EmtTypography variant={'h4'} sx={{ marginBottom: isMobile ? 'auto' : '10px' }} fontWeight="800">
            {t(`noPremium.title.${queryType}`)}
          </EmtTypography>

          <EmtTypography variant="body1" fontWeight="700">
            {t(`noPremium.content1.${queryType}`)}
          </EmtTypography>
          {activeSecondaryContent && (
            <EmtTypography variant="body1">{t(`noPremium.content2.${queryType}`)}</EmtTypography>
          )}
          <Box
            {...(isMobile ? { sx: { textAlign: 'center', width: '100%' } } : {})}
            className="EmtReactNativeWebViewPaddingBottom"
          >
            <LinkButton
              fullWidth={isMobile}
              size="large"
              color="secondary"
              startIcon={<EmtIcon className="emt-crown" />}
              sx={{ paddingTop: '10px', paddingBottom: '10px' }}
              href={appendQueryType(Routes.Premium.Base)}
            >
              {t('noPremium.discover')}
            </LinkButton>
          </Box>
        </NoPremiumBox>
      </Box>
    </EmtModal>
  )
}

export default PremiumModal
